<template>
  <Module
    v-if="hasContent"
    id="historyOfThisArtwork"
    ref="tabPanel"
    class="o-human-made-object-tabs"
    :title="heading"
    :intro="hasDeaccessionNotice ? deaccessionNotice : undefined"
    :modifiers="['human-made-object-tabs']"
    :cta="
      hasDeaccessionNotice
        ? {
            href: '/museum/deaccessioning',
            text: deaccessionLinkText
          }
        : undefined
    "
  >
    <div class="o-human-made-object-tabs__tablist" :aria-label="ariaPanelLabel">
      <div class="o-human-made-object-tabs__sidebar media-print-hidden">
        <h3 id="aria-panel-section" class="visually-hidden">
          {{ ariaPanelSelectionLabel }}
        </h3>
        <ul role="tablist" :aria-labelledby="'aria-panel-section'">
          <HumanMadeObjectTab
            v-if="hasProvenance"
            id="provenance"
            :tab-name="provenanceTabName"
            tab-key="provenance"
            :is-selected="currentTab == 'provenance'"
            :credits-active="creditsActiveTab == 'provenance'"
            @change-tab="changeTab"
            @credits-active="creditsActiveTab = $event"
          />
          <HumanMadeObjectTab
            v-if="hasBibliography"
            :tab-name="bibliographyTabName"
            tab-key="bibliography"
            :is-selected="currentTab == 'bibliography'"
            :credits-active="creditsActiveTab == 'bibliography'"
            @change-tab="changeTab"
            @credits-active="creditsActiveTab = $event"
          />
          <HumanMadeObjectTab
            v-if="hasExhibitions"
            :tab-name="exhibitionsTabName"
            tab-key="exhibitions"
            :is-selected="currentTab == 'exhibitions'"
            :credits-active="creditsActiveTab == 'exhibitions'"
            @change-tab="changeTab"
            @credits-active="creditsActiveTab = $event"
          />
        </ul>
      </div>
      <div class="o-human-made-object-tabs__content">
        <ProvenanceList
          v-if="hasProvenance"
          id="provenance_tab"
          ref="provenanceTab"
          :class="getTabClasses('provenance')"
          :indexed-id="humanMadeObject.id"
          :provenance-info="humanMadeObject.provenance"
          role="tabpanel"
        />
        <ExhibitionList
          v-if="hasExhibitions"
          id="exhibitions_tab"
          ref="exhibitionsTab"
          :class="getTabClasses('exhibitions')"
          :exhibitions="humanMadeObject.exhibitions"
          role="tabpanel"
        />
        <BibliographyList
          v-if="hasBibliography"
          id="bibliography_tab"
          ref="bibliographyTab"
          :class="getTabClasses('bibliography')"
          :bibliography="humanMadeObject.bibliography"
          role="tabpanel"
        />
      </div>
    </div>
  </Module>
</template>

<script>
import BibliographyList from "@/components/molecules/bibliographyList/index.vue";
import ExhibitionList from "@/components/molecules/exhibitionList/index.vue";
import ProvenanceList from "@/components/molecules/provenanceList/index.vue";
import {
  Module,
  useLocalization,
  useEventBus
} from "@thegetty-private/getty-ui";
import HumanMadeObjectTab from "./tab.vue";
import { routerHelpers } from "@thegetty-private/rad-app-utils";

export default {
  name: "HumanMadeObjectTabs",
  components: {
    BibliographyList,
    ExhibitionList,
    Module,
    ProvenanceList,
    HumanMadeObjectTab
  },

  props: {
    /**
     * The HumanMadeObject metadata from the store
     */
    humanMadeObject: {
      type: Object,
      required: true
    },
    /**
     * the current slug
     */
    slug: {
      type: String,
      required: false,
      default: undefined
    }
  },
  emits: ["setTab"],
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * The aria label for the panel
       */
      ariaPanelLabel: this.translatePart(
        "human_made_object_tabs.ariaPanelLabel"
      ),
      /**
       * The aria label for the panel selector
       */
      ariaPanelSelectionLabel: this.translatePart(
        "human_made_object_tabs.ariaPanelSelectionLabel"
      ),
      /**
       * label for the bibliography tab
       */
      bibliographyTabName: this.translatePart(
        "human_made_object_tabs.tab.bibliography.label"
      ),
      /**
       * The current or selected tab
       */
      currentTab: "provenance",
      /**
       * The text for the deaccession notice
       */
      deaccessionNotice: this.translatePart(
        "human_made_object_tabs.deaccessionNotice"
      ),
      /**
       * The text for the deaccession policy link
       */
      deaccessionLinkText: this.translatePart(
        "human_made_object_tabs.deaccessionLinkText"
      ),
      /**
       * label for the exhbitions tab
       */
      exhibitionsTabName: this.translatePart(
        "human_made_object_tabs.tab.exhibitions.label"
      ),
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus(),
      /**
       * The heading of the component
       */
      heading: this.translatePart("human_made_object_tabs.heading"),
      /**
       * label for the provenance tab
       */
      provenanceTabName: this.translatePart(
        "human_made_object_tabs.tab.provenance.label"
      ),
      /**
       * tab key for currently active credits component (i.e., tab tooltip)
       */
      creditsActiveTab: false
    };
  },
  computed: {
    /**
     * Whether there's bibiographic data
     *
     * @returns {boolean}
     */
    hasBibliography() {
      return this.humanMadeObject?.bibliography?.length > 0;
    },
    /**
     * Whether there's any tab content at all
     */
    hasContent() {
      if (this.hasBibliography || this.hasExhibitions || this.hasProvenance) {
        return true;
      }
      return false;
    },
    /**
     * Whether to display the deaccession notice text and link
     */
    hasDeaccessionNotice() {
      return this.humanMadeObject?.isDeaccessioned ? true : false;
    },
    /**
     * Whether there's exhibtion data
     *
     * @returns {boolean}
     */
    hasExhibitions() {
      return this.humanMadeObject?.exhibitions?.length > 0;
    },
    /**
     * Whether there's provenance data
     *
     * @returns {boolean}
     */
    hasProvenance() {
      return (
        this.humanMadeObject?.provenance?.data?.length > 0 ||
        this.humanMadeObject?.provenance?.notes?.length > 0
      );
    }
  },
  watch: {
    $route() {
      if ("query" in this.$route && this.$route.query.tab) {
        this.currentTab = this.$route.query.tab;
      }
    }
  },
  mounted() {
    if (
      this.hasContent &&
      this.$route &&
      "query" in this.$route &&
      this.$route.query.tab
    ) {
      this.currentTab = this.$route.query.tab;
    } else {
      // choose the current tab
      if (this.hasProvenance) {
        this.currentTab = "provenance";
      } else if (this.hasBibliography) {
        this.currentTab = "bibliography";
      } else if (this.hasExhibitions) {
        this.currentTab = "exhibitions";
      }
    }
  },
  methods: {
    /**
     * Changes the active tab to the specified tab name
     *
     * @param {string} tabName -- the name of the tab
     */
    changeTab(tabName) {
      const currentQuery = this.$route.query ?? {};
      if (this.currentTab != tabName) {
        this.currentTab = tabName;
        this.creditsActiveTab = undefined;
        this.$emit("setTab", this.currentTab);
        this.eventBus.emit("AnalyticsEvent", {
          action: "Details Click",
          category: tabName,
          label: this.humanMadeObject?.id,
          noninteraction: true
        });
        let slug = this.slug;
        if (slug == undefined) {
          slug = this.$route.params.slug;
        }
        routerHelpers.routerReplace(this.$router, {
          name: "object",
          query: { ...currentQuery, tab: this.currentTab },
          params: {
            slug: slug
          }
        });
      }
    },
    /**
     * Gets the tab classes for the selected tab
     *
     * @param {string} - the tab name to test
     *
     * @returns {string|undefined}
     */
    getTabClasses(tabName) {
      if (this.currentTab != tabName) {
        return "hidden-media-print-visible";
      }
      return undefined;
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
